/**
 * Static demo report — matches the production v2 design language exactly.
 * 4 switchable variants (Good / Bad / Combined / Pre-Screen) via the right sidebar.
 * Accessible at /demo-report after the demo application is submitted.
 */

import {
	Baby,
	Banknote,
	CalendarDays,
	Car,
	ChevronLeft,
	CircleDollarSign,
	ClipboardPlus,
	Crown,
	FileText,
	FileUser,
	FlagTriangleRight,
	HandCoins,
	Home,
	PawPrint,
	Shield,
	ShieldCheck,
	Sparkle,
	TrendingUp,
	User,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'

// ── Types ──────────────────────────────────────────────────────────────────────

type ReportVariant = 'good' | 'bad' | 'combined' | 'prescreen' | 'guarantor'
type BarTone = 'green' | 'amber' | 'red' | 'neutral'
type PastelTone = 'green' | 'rose' | 'purple' | 'amber' | 'red' | 'neutral'

interface VerifTile {
	status: BarTone
	text: string
	onClick?: () => void
}

interface FlagItem {
	level: 'red' | 'yellow'
	message: string
}

interface Tx {
	date: string
	desc: string
	amount: string
	balance: string
	positive: boolean
}

interface CollectionRow {
	creditor: string
	balance: string
	opened: string
	status: string
	remarks: string
}

interface ReportData {
	// Header
	applicantName: string
	initials: string
	subtitle: string
	statusLabel: string
	statusTone: PastelTone
	/** Drives which sections render: pre-screen shows only 3 verif tiles + step-data */
	kind: 'full' | 'prescreen'
	// Score (full only)
	score: number
	showGuarantee: boolean
	guaranteeLabel: string
	showGuarantor: boolean
	guarantorLabel: string
	guarantorTone: 'green' | 'amber' | 'red'
	// Summary tiles
	rentalRange: string
	moveInDate: string
	savings: string
	// Verification
	verifs: {
		id: VerifTile
		income: VerifTile
		rent: VerifTile
		court: VerifTile
		credit: VerifTile
	}
	// AI Summary
	aiSummary: string
	// Risk Flags
	redFlags: string[]
	yellowFlags: string[]
	// Debt
	monthlyDebt: string
	totalOwing: string
	// Bank Monitoring
	avgBalance: string
	incomeToRent: string
	overdrafts: string
	transactions: Tx[]
	// Collections
	collections: CollectionRow[]
	// Personal
	firstName: string
	lastName: string
	email: string
	phone: string
	appDate: string
	appType: string
	// Household
	adults: string
	children: string
	pets: string
	vehicles: string
	// Employment
	curCompany: string
	curOccupation: string
	curAnnualIncome: string
	curEmploymentType: string
	curYears: string
	curWorkAddress: string
	prevCompany: string
	prevOccupation: string
	prevAnnualIncome: string
	prevDates: string
	// Rental
	landlordName: string
	landlordPhone: string
	landlordEmail: string
	curRentAddress: string
	curRent: string
	curPaymentMethod: string
	rentalDuration: string
	n4: string
	l1: string
	prevLandlord: string
	prevRentAddress: string
	prevRent: string
	prevPaymentMethod: string
	prevRentalDates: string
	prevN4: string
	prevL1: string
	// Notes
	additionalNotes: string
}

// ── Report data ────────────────────────────────────────────────────────────────

const GOOD: ReportData = {
	applicantName: 'John Carter',
	initials: 'JC',
	subtitle: '290 Bremner Blvd, Toronto, ON · CN Tower Residences, Unit 6',
	statusLabel: 'Approved',
	statusTone: 'green',
	kind: 'full',
	score: 90,
	showGuarantee: true,
	guaranteeLabel: 'Rental Guarantee',
	showGuarantor: false,
	guarantorLabel: '',
	guarantorTone: 'green',
	rentalRange: '$3,000 – $4,000 / mo',
	moveInDate: 'June 1, 2026',
	savings: '$85,000',
	verifs: {
		id: {
			status: 'green',
			text: 'Verified',
			onClick: () => window.open('/img/dummy-id.jpg', '_blank'),
		},
		income: {
			status: 'green',
			text: 'View',
			onClick: () => window.open('/demo-income-verification.html', '_blank'),
		},
		rent: {
			status: 'green',
			text: 'View',
			onClick: () => window.open('/demo-rent-verification.html', '_blank'),
		},
		court: { status: 'green', text: 'Completed' },
		credit: {
			status: 'green',
			text: 'View',
			onClick: () => window.open('/individual-report.html', '_blank'),
		},
	},
	aiSummary:
		'John Carter is a full-time Software Engineer earning $150,000 per year at TrueTenant Inc., where he has worked for 4 years. Income is verified against stable semi-monthly payroll deposits via Flinks (avg. balance $85,000), there are no collections or court records, and his current landlord Michael Thompson confirms 3 years and 4 months of on-time rent paid via Pre-Authorized Debit. No N4 or L1 notices on file. Credit utilization is low at 12%. Low-risk applicant — well within affordability for this unit.',
	redFlags: [],
	yellowFlags: [],
	monthlyDebt: '$850',
	totalOwing: '$6,400',
	avgBalance: '$85,000',
	incomeToRent: '3.9×',
	overdrafts: '0',
	transactions: [
		{
			date: 'May 30, 2026',
			desc: 'Payroll — TrueTenant Inc.',
			amount: '+$6,250',
			balance: '$87,400',
			positive: true,
		},
		{
			date: 'May 15, 2026',
			desc: 'Payroll — TrueTenant Inc.',
			amount: '+$6,250',
			balance: '$81,150',
			positive: true,
		},
		{
			date: 'May 2, 2026',
			desc: 'Rent — 55 Harbour Square',
			amount: '-$3,200',
			balance: '$74,900',
			positive: false,
		},
		{
			date: 'Apr 30, 2026',
			desc: 'Payroll — TrueTenant Inc.',
			amount: '+$6,250',
			balance: '$78,100',
			positive: true,
		},
		{
			date: 'Apr 15, 2026',
			desc: 'Payroll — TrueTenant Inc.',
			amount: '+$6,250',
			balance: '$71,850',
			positive: true,
		},
	],
	collections: [],
	firstName: 'John',
	lastName: 'Carter',
	email: 'john.carter@truetenant.ai',
	phone: '+1 (416) 111-1111',
	appDate: 'June 23, 2026',
	appType: 'Primary Applicant',
	adults: '2',
	children: '1 — DOB March 14, 2020',
	pets: 'Dog · 15–30 lbs',
	vehicles: 'Car / SUV · Plate BTRK 740',
	curCompany: 'TrueTenant Inc.',
	curOccupation: 'Software Engineer',
	curAnnualIncome: '$150,000',
	curEmploymentType: 'Full-Time',
	curYears: '4 Years',
	curWorkAddress: '250 University Ave, Toronto, ON',
	prevCompany: 'Shopify',
	prevOccupation: 'Software Developer',
	prevAnnualIncome: '$120,000',
	prevDates: 'Jun 1, 2018 – Dec 31, 2021',
	landlordName: 'Michael Thompson',
	landlordPhone: '+1 (416) 444-4444',
	landlordEmail: 'michaellandlord@truetenant.ai',
	curRentAddress: '55 Harbour Square, Toronto, ON',
	curRent: '$3,200',
	curPaymentMethod: 'Pre-Authorized Debit',
	rentalDuration: '3 Years 4 Months',
	n4: 'No',
	l1: 'No',
	prevLandlord: 'Jennifer Lewis',
	prevRentAddress: '100 Queens Quay E, Toronto, ON',
	prevRent: '$2,600',
	prevPaymentMethod: 'e-Transfer',
	prevRentalDates: 'Jan 1, 2019 – Feb 28, 2022',
	prevN4: 'No',
	prevL1: 'No',
	additionalNotes: '',
}

const BAD: ReportData = {
	applicantName: 'Tyler Brooks',
	initials: 'TB',
	subtitle: '1 York St, Toronto, ON · Lakefront Suites, Unit 3B',
	statusLabel: 'Declined',
	statusTone: 'red',
	kind: 'full',
	score: 36,
	showGuarantee: true,
	guaranteeLabel: 'Rental Guarantee',
	showGuarantor: true,
	guarantorLabel: 'Guarantor Required',
	guarantorTone: 'red',
	rentalRange: 'Not Recommended',
	moveInDate: 'July 1, 2026',
	savings: '$1,200',
	verifs: {
		id: {
			status: 'green',
			text: 'Verified',
			onClick: () => window.open('/img/dummy-id.jpg', '_blank'),
		},
		income: { status: 'red', text: 'Unverified' },
		rent: {
			status: 'amber',
			text: 'Partial',
			onClick: () => window.open('/demo-rent-verification.html', '_blank'),
		},
		court: { status: 'red', text: 'Flagged' },
		credit: {
			status: 'neutral',
			text: 'Poor — 512',
			onClick: () => window.open('/individual-report.html', '_blank'),
		},
	},
	aiSummary:
		'Tyler Brooks is applying as a primary tenant. Declared employment income of $42,000/yr from Cash Depot could not be verified through banking data — deposits are irregular and do not match a consistent payroll pattern. A current landlord N4 notice was filed in February 2026 for non-payment of rent, and a prior L1 eviction application was filed in 2024. Credit score of 512 reflects 2 active collections totalling $2,100. Average account balance over the past 90 days is $1,200, with 4 NSF events noted. Calculated income-to-rent ratio of 1.6× falls significantly below the recommended 3× threshold. High-risk applicant — not recommended for this unit without a substantial co-signer and additional deposit.',
	redFlags: [
		'N4 notice filed by current landlord Patricia Okafor — February 2026 (non-payment of rent)',
		'L1 eviction application on record — previous tenancy 2024',
		'2 active collections — Rogers Wireless $1,300 · Snap Financial $800 (total $2,100)',
	],
	yellowFlags: [
		'Irregular income deposits — no consistent payroll pattern detected in banking data',
		'4 NSF charges recorded in the last 90 days',
	],
	monthlyDebt: '$1,420',
	totalOwing: '$9,200',
	avgBalance: '$1,200',
	incomeToRent: '1.6×',
	overdrafts: '4',
	transactions: [
		{
			date: 'Jun 5, 2026',
			desc: 'Cash Deposit',
			amount: '+$800',
			balance: '$1,420',
			positive: true,
		},
		{
			date: 'Jun 1, 2026',
			desc: 'NSF Return Fee',
			amount: '-$45',
			balance: '$620',
			positive: false,
		},
		{
			date: 'May 28, 2026',
			desc: 'Misc Transfer In',
			amount: '+$1,200',
			balance: '$665',
			positive: true,
		},
		{
			date: 'May 15, 2026',
			desc: 'Rogers Wireless (Collection)',
			amount: '-$130',
			balance: '-$535',
			positive: false,
		},
		{
			date: 'May 1, 2026',
			desc: 'Rent e-Transfer',
			amount: '-$1,800',
			balance: '$405',
			positive: false,
		},
	],
	collections: [
		{
			creditor: 'Rogers Wireless',
			balance: '$1,300',
			opened: 'Oct 2025',
			status: 'Active',
			remarks: 'Unpaid telecommunications account',
		},
		{
			creditor: 'Snap Financial',
			balance: '$800',
			opened: 'Mar 2026',
			status: 'Active',
			remarks: 'Unpaid consumer loan',
		},
	],
	firstName: 'Tyler',
	lastName: 'Brooks',
	email: 'tyler.brooks@email.com',
	phone: '+1 (647) 222-2222',
	appDate: 'June 23, 2026',
	appType: 'Primary Applicant',
	adults: '1',
	children: 'None',
	pets: 'None declared',
	vehicles: 'None declared',
	curCompany: 'Cash Depot (Part-Time)',
	curOccupation: 'Sales Associate',
	curAnnualIncome: '$42,000 (declared)',
	curEmploymentType: 'Part-Time',
	curYears: '8 Months',
	curWorkAddress: '1200 Sheppard Ave W, Toronto, ON',
	prevCompany: 'Self-Employed (Gig Work)',
	prevOccupation: 'Delivery / Misc.',
	prevAnnualIncome: 'Varies',
	prevDates: 'Jan 2022 – Oct 2025',
	landlordName: 'Patricia Okafor',
	landlordPhone: '+1 (416) 999-1234',
	landlordEmail: 'patricia.o@email.com',
	curRentAddress: '740 Lawrence Ave W, Toronto, ON',
	curRent: '$1,800',
	curPaymentMethod: 'e-Transfer (intermittent)',
	rentalDuration: '1 Year 2 Months',
	n4: 'Yes — Feb 2026',
	l1: 'No',
	prevLandlord: 'David Ferreira',
	prevRentAddress: '22 Crescent Rd, Toronto, ON',
	prevRent: '$1,600',
	prevPaymentMethod: 'Cash',
	prevRentalDates: 'Mar 2022 – Nov 2023',
	prevN4: 'No',
	prevL1: 'Yes — 2024 (resolved)',
	additionalNotes:
		'I am looking to start fresh and have been working on improving my financial situation over the past few months.',
}

const COMBINED: ReportData = {
	applicantName: 'Jordan & Casey Rivera',
	initials: 'JR',
	subtitle: '80 Queens Quay W, Toronto, ON · Harbourview Commons, Unit 12A',
	statusLabel: 'Conditionally Approved',
	statusTone: 'amber',
	kind: 'full',
	score: 68,
	showGuarantee: true,
	guaranteeLabel: 'Rental Guarantee',
	showGuarantor: true,
	guarantorLabel: 'Guarantor Possible',
	guarantorTone: 'amber',
	rentalRange: '$2,500 – $3,500 / mo',
	moveInDate: 'August 1, 2026',
	savings: 'Combined $31,000',
	verifs: {
		id: {
			status: 'green',
			text: 'Verified',
			onClick: () => window.open('/img/dummy-id.jpg', '_blank'),
		},
		income: {
			status: 'amber',
			text: 'Partial',
			onClick: () => window.open('/demo-income-verification.html', '_blank'),
		},
		rent: {
			status: 'green',
			text: 'View',
			onClick: () => window.open('/demo-rent-verification.html', '_blank'),
		},
		court: { status: 'green', text: 'Completed' },
		credit: {
			status: 'green',
			text: 'Good — 695',
			onClick: () => window.open('/individual-report.html', '_blank'),
		},
	},
	aiSummary:
		"Jordan and Casey Rivera are applying as co-applicants. Jordan Rivera is currently employed as a Project Coordinator at Maple Logistics ($62,000/yr), verified via consistent bi-weekly Flinks payroll deposits. A 3-month employment gap (Sep–Nov 2025) was disclosed and is consistent with banking data — Jordan has been continuously employed since December 2025. Casey Rivera's income of $38,000/yr from freelance graphic design is self-reported and could not be confirmed through banking patterns alone. Combined reported income of $100,000/yr gives an income-to-rent ratio of approximately 2.7×. No court records or collections on file. Combined average balance of $31,000. Conditionally approved — recommend requesting Casey's most recent 3 months of bank statements or invoices to confirm freelance income before finalising.",
	redFlags: [],
	yellowFlags: [
		"Co-applicant Casey Rivera's income ($38,000/yr) is self-reported only — bank verification not completed",
		'Primary applicant Jordan Rivera had a 3-month employment gap (Sep–Nov 2025) — currently re-employed for 7 months',
	],
	monthlyDebt: '$1,100',
	totalOwing: '$10,400',
	avgBalance: '$31,000',
	incomeToRent: '2.7×',
	overdrafts: '0',
	transactions: [
		{
			date: 'Jun 3, 2026',
			desc: 'Payroll — Maple Logistics',
			amount: '+$2,583',
			balance: '$32,400',
			positive: true,
		},
		{
			date: 'May 20, 2026',
			desc: 'Payroll — Maple Logistics',
			amount: '+$2,583',
			balance: '$29,817',
			positive: true,
		},
		{
			date: 'May 1, 2026',
			desc: 'Rent — 880 Bay St',
			amount: '-$2,400',
			balance: '$27,234',
			positive: false,
		},
		{
			date: 'Apr 30, 2026',
			desc: 'Payroll — Maple Logistics',
			amount: '+$2,583',
			balance: '$29,634',
			positive: true,
		},
		{
			date: 'Apr 3, 2026',
			desc: 'Transfer In (Casey — freelance)',
			amount: '+$3,166',
			balance: '$27,051',
			positive: true,
		},
	],
	collections: [],
	firstName: 'Jordan',
	lastName: 'Rivera (+ Casey Rivera)',
	email: 'jordan.rivera@truetenant.ai',
	phone: '+1 (416) 500-7777',
	appDate: 'June 23, 2026',
	appType: 'Co-Applicant (2 persons)',
	adults: '2',
	children: 'None',
	pets: 'Cat · under 10 lbs',
	vehicles: 'Car / SUV · Plate MPLX 219',
	curCompany: 'Maple Logistics',
	curOccupation: 'Project Coordinator',
	curAnnualIncome:
		'$62,000 (Jordan — bank-verified) + $38,000 (Casey — self-reported)',
	curEmploymentType: 'Full-Time (Jordan) / Freelance (Casey)',
	curYears: '7 Months re-employed (Jordan)',
	curWorkAddress: '401 Bay St, Toronto, ON',
	prevCompany: 'Employment gap Sep–Nov 2025 (Jordan)',
	prevOccupation: 'N/A',
	prevAnnualIncome: 'N/A',
	prevDates: 'Sep 2025 – Nov 2025',
	landlordName: 'Sarah Nguyen',
	landlordPhone: '+1 (416) 488-2222',
	landlordEmail: 'sarah.nguyen@landlord.ai',
	curRentAddress: '880 Bay St, Toronto, ON',
	curRent: '$2,400',
	curPaymentMethod: 'Pre-Authorized Debit',
	rentalDuration: '2 Years 1 Month',
	n4: 'No',
	l1: 'No',
	prevLandlord: 'Mark Osei',
	prevRentAddress: '200 Victoria St, Toronto, ON',
	prevRent: '$2,000',
	prevPaymentMethod: 'e-Transfer',
	prevRentalDates: 'May 2021 – Apr 2024',
	prevN4: 'No',
	prevL1: 'No',
	additionalNotes:
		"Casey's freelance clients include two Toronto agencies — can provide invoices and T4As on request.",
}

const PRESCREEN: ReportData = {
	applicantName: 'Sarah Kim',
	initials: 'SK',
	subtitle: '45 Oak St, Toronto, ON · Maple Grove Apartments, Unit 8C',
	statusLabel: 'Pre-Screening Complete',
	statusTone: 'purple',
	kind: 'prescreen',
	score: 0,
	showGuarantee: false,
	guaranteeLabel: '',
	showGuarantor: false,
	guarantorLabel: '',
	guarantorTone: 'green',
	rentalRange: '$2,800 – $3,800 / mo',
	moveInDate: 'Aug 1, 2026',
	savings: '$22,000',
	verifs: {
		id: {
			status: 'green',
			text: 'Verified',
			onClick: () => window.open('/img/dummy-id.jpg', '_blank'),
		},
		income: { status: 'amber', text: 'Pending' },
		rent: {
			status: 'green',
			text: 'Verified',
			onClick: () => window.open('/demo-rent-verification.html', '_blank'),
		},
		court: { status: 'neutral', text: 'Not Available' },
		credit: { status: 'neutral', text: 'Not Available' },
	},
	aiSummary: '',
	redFlags: [],
	yellowFlags: [],
	monthlyDebt: '',
	totalOwing: '',
	avgBalance: '',
	incomeToRent: '',
	overdrafts: '',
	transactions: [],
	collections: [],
	firstName: 'Sarah',
	lastName: 'Kim',
	email: 'sarah.kim@truetenant.ai',
	phone: '+1 (416) 600-2200',
	appDate: 'June 23, 2026',
	appType: 'Primary Applicant',
	adults: '1',
	children: 'None',
	pets: 'Cat · under 10 lbs',
	vehicles: 'None declared',
	curCompany: 'Bloom Creative Agency',
	curOccupation: 'Marketing Coordinator',
	curAnnualIncome: '$72,000',
	curEmploymentType: 'Full-Time',
	curYears: '2 Years 6 Months',
	curWorkAddress: '220 King St W, Toronto, ON',
	prevCompany: 'MediaPulse Inc.',
	prevOccupation: 'Junior Marketer',
	prevAnnualIncome: '$58,000',
	prevDates: 'Jun 2020 – Dec 2022',
	landlordName: 'Kevin Park',
	landlordPhone: '+1 (416) 750-3300',
	landlordEmail: 'kevin.park@landlord.ai',
	curRentAddress: '12 Elm Ave, Toronto, ON',
	curRent: '$2,200',
	curPaymentMethod: 'e-Transfer',
	rentalDuration: '1 Year 6 Months',
	n4: 'No',
	l1: 'No',
	prevLandlord: 'Grace Huang',
	prevRentAddress: '88 Bloor St W, Toronto, ON',
	prevRent: '$1,800',
	prevPaymentMethod: 'Cheque',
	prevRentalDates: 'Jan 2021 – Dec 2023',
	prevN4: 'No',
	prevL1: 'No',
	additionalNotes:
		'I am a quiet professional who works from home 2 days a week. I have a small cat and am looking for a long-term rental.',
}

const GUARANTOR: ReportData = {
	applicantName: 'Robert Carter',
	initials: 'RC',
	subtitle: 'Guarantor for John Carter · CN Tower Residences, Unit 6',
	statusLabel: 'Guarantor Approved',
	statusTone: 'green',
	kind: 'full',
	score: 94,
	showGuarantee: false,
	guaranteeLabel: '',
	showGuarantor: true,
	guarantorLabel: 'Backing John Carter',
	guarantorTone: 'green',
	rentalRange: 'Covers $3,000 – $4,000 / mo',
	moveInDate: 'June 1, 2026',
	savings: '$240,000',
	verifs: {
		id: {
			status: 'green',
			text: 'Verified',
			onClick: () => window.open('/img/dummy-id.jpg', '_blank'),
		},
		income: {
			status: 'green',
			text: 'View',
			onClick: () => window.open('/demo-income-verification.html', '_blank'),
		},
		rent: { status: 'neutral', text: 'Homeowner' },
		court: { status: 'green', text: 'Completed' },
		credit: {
			status: 'green',
			text: 'View',
			onClick: () => window.open('/individual-report.html', '_blank'),
		},
	},
	aiSummary:
		'Robert Carter is acting as guarantor for the primary applicant, John Carter. He is a full-time Director of Engineering earning $215,000 per year at Northbridge Systems (9 years tenure), with income verified against stable payroll deposits via Flinks (avg. balance $240,000). He owns his residence outright, has no collections or court records, and carries low credit utilization (8%). His verified capacity comfortably covers the unit rent of $3,200/mo at well above the 3× threshold even as a secondary obligation. Strong guarantor — recommended.',
	redFlags: [],
	yellowFlags: [],
	monthlyDebt: '$1,200',
	totalOwing: '$0',
	avgBalance: '$240,000',
	incomeToRent: '5.6×',
	overdrafts: '0',
	transactions: [
		{
			date: 'May 30, 2026',
			desc: 'Payroll — Northbridge Systems',
			amount: '+$8,958',
			balance: '$242,300',
			positive: true,
		},
		{
			date: 'May 15, 2026',
			desc: 'Payroll — Northbridge Systems',
			amount: '+$8,958',
			balance: '$233,342',
			positive: true,
		},
		{
			date: 'May 1, 2026',
			desc: 'Mortgage — paid off',
			amount: '$0',
			balance: '$224,384',
			positive: true,
		},
		{
			date: 'Apr 30, 2026',
			desc: 'Payroll — Northbridge Systems',
			amount: '+$8,958',
			balance: '$224,384',
			positive: true,
		},
	],
	collections: [],
	firstName: 'Robert',
	lastName: 'Carter',
	email: 'robert.carter@truetenant.ai',
	phone: '+1 (905) 333-3333',
	appDate: 'June 23, 2026',
	appType: 'Guarantor for John Carter',
	adults: '—',
	children: '—',
	pets: '—',
	vehicles: '—',
	curCompany: 'Northbridge Systems',
	curOccupation: 'Director of Engineering',
	curAnnualIncome: '$215,000',
	curEmploymentType: 'Full-Time',
	curYears: '9 Years',
	curWorkAddress: '100 King St W, Toronto, ON',
	prevCompany: 'Rogers Communications',
	prevOccupation: 'Engineering Manager',
	prevAnnualIncome: '$175,000',
	prevDates: 'Jan 2012 – Mar 2017',
	landlordName: '—',
	landlordPhone: '—',
	landlordEmail: '—',
	curRentAddress: '88 Forest Hill Rd, Toronto, ON (Owned)',
	curRent: 'Homeowner — no rent',
	curPaymentMethod: '—',
	rentalDuration: '12 Years (Owned)',
	n4: 'No',
	l1: 'No',
	prevLandlord: '—',
	prevRentAddress: '—',
	prevRent: '—',
	prevPaymentMethod: '—',
	prevRentalDates: '—',
	prevN4: 'No',
	prevL1: 'No',
	additionalNotes:
		'I am happy to act as guarantor for my son John. I own my home and have stable long-term employment.',
}

const REPORT_DATA: Record<ReportVariant, ReportData> = {
	good: GOOD,
	bad: BAD,
	combined: COMBINED,
	prescreen: PRESCREEN,
	guarantor: GUARANTOR,
}

// ── Design primitives (mirror production design language) ─────────────────────

const CARD =
	'rounded-3xl bg-[#fbfdfd] shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]'

const PASTEL_TONES: Record<PastelTone, string> = {
	green: 'bg-[#D6E5BE40] text-[#A5BB83]',
	rose: 'bg-[#fbf5f8] text-[#BE7B97]',
	purple: 'bg-[#efeaf6] text-[#9976B8]',
	amber: 'bg-[#FFF6EA] text-[#F59F0A]',
	red: 'bg-[#FDF4F5] text-[#DC2634]',
	neutral: 'bg-gray-100 text-gray-600',
}

const CHIP_TONES: Record<PastelTone, string> = PASTEL_TONES

const BAR_TONES: Record<BarTone, string> = {
	green:   'border-t-green-600',
	amber:   'border-t-[#F59F0A]',
	red:     'border-t-red-500',
	neutral: '',
}

function IconSquare({
	tone,
	children,
	className = '',
}: { tone: PastelTone; children: React.ReactNode; className?: string }) {
	return (
		<span
			className={`flex size-12 shrink-0 items-center justify-center rounded-xl [&>svg]:size-5 ${PASTEL_TONES[tone]} ${className}`}
		>
			{children}
		</span>
	)
}

function FieldBox({ label, value }: { label: string; value?: string | null }) {
	return (
		<div className="rounded-xl border border-[#F0F0F0] bg-[#fbfdfd] px-4 py-3">
			<span className="block text-[13px] text-[#747474]">{label}</span>
			<span className="text-sm font-medium text-[#32302F]">{value || '—'}</span>
		</div>
	)
}

function SectionCard({
	tone,
	icon,
	title,
	children,
}: {
	tone: PastelTone
	icon: React.ReactNode
	title: string
	children: React.ReactNode
}) {
	return (
		<section className={`${CARD} p-6 sm:p-7`}>
			<div className="flex items-center gap-3">
				<IconSquare tone={tone}>{icon}</IconSquare>
				<h2 className="text-[16px] font-semibold text-[#32302F]">{title}</h2>
			</div>
			<div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
				{children}
			</div>
		</section>
	)
}

// ── Demo sidebar ───────────────────────────────────────────────────────────────

const VARIANT_OPTIONS: {
	id: ReportVariant
	label: string
	sub: string
	dot: string
}[] = [
	{
		id: 'prescreen',
		label: 'Pre-Screen',
		sub: 'Limited View',
		dot: 'bg-purple-400',
	},
	{
		id: 'good',
		label: 'Strong Applicant',
		sub: 'Tier 1 · 90/100',
		dot: 'bg-green-500',
	},
	{ id: 'bad', label: 'High Risk', sub: 'Tier 4 · 36/100', dot: 'bg-red-500' },
	{
		id: 'combined',
		label: 'Mixed Signals',
		sub: 'Tier 2 · 68/100',
		dot: 'bg-amber-400',
	},
	{
		id: 'guarantor',
		label: 'Guarantor Report',
		sub: 'Backing · 94/100',
		dot: 'bg-green-500',
	},
]

const VARIANTS = new Set<ReportVariant>([
	'good',
	'bad',
	'combined',
	'prescreen',
	'guarantor',
])

function parseVariant(value: string | null): ReportVariant {
	return value && VARIANTS.has(value as ReportVariant)
		? (value as ReportVariant)
		: 'prescreen'
}

function DemoSidebar({
	current,
	onChange,
}: { current: ReportVariant; onChange: (v: ReportVariant) => void }) {
	return (
		<>
			<div className="hidden lg:flex fixed right-0 top-16 bottom-0 w-49 bg-[#fbfdfd] border-l border-[#EDEDEB] flex-col gap-2 p-4 z-40 overflow-y-auto">
				<p className="text-[10px] font-semibold uppercase tracking-wider text-[#747474] mb-1">
					Demo Reports
				</p>
				{VARIANT_OPTIONS.map((opt) => {
					const active = current === opt.id
					return (
						<button
							key={opt.id}
							type="button"
							onClick={() => onChange(opt.id)}
							className={`text-left rounded-2xl border px-3 py-3 transition-all ${
								active
									? 'border-[#32302F] bg-[#32302F]'
									: 'border-[#F0F0F0] bg-[#fbfdfd] hover:border-[#AAAAAA]'
							}`}
						>
							<div className="flex items-center gap-2 mb-1.5">
								<span
									className={`w-2 h-2 rounded-full shrink-0 ${active ? 'bg-white opacity-60' : opt.dot}`}
								/>
								<span
									className={`text-[11px] font-semibold uppercase tracking-wide ${active ? 'text-gray-300' : 'text-[#747474]'}`}
								>
									{opt.sub}
								</span>
							</div>
							<p
								className={`text-sm font-semibold leading-tight ${active ? 'text-white' : 'text-[#32302F]'}`}
							>
								{opt.label}
							</p>
						</button>
					)
				})}
			</div>
			<div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#fbfdfd] border-t border-[#EDEDEB] px-3 py-2.5 flex gap-2 z-40">
				{VARIANT_OPTIONS.map((opt) => {
					const active = current === opt.id
					return (
						<button
							key={opt.id}
							type="button"
							onClick={() => onChange(opt.id)}
							className={`flex-1 rounded-xl border px-2 py-2 text-xs font-medium transition-all ${
								active
									? 'border-[#32302F] bg-[#32302F] text-white'
									: 'border-[#F0F0F0] bg-[#fbfdfd] text-[#32302F]'
							}`}
						>
							<div
								className={`text-[10px] mb-0.5 ${active ? 'text-gray-300' : 'text-[#747474]'}`}
							>
								{opt.sub.split('·')[1]}
							</div>
							{opt.label}
						</button>
					)
				})}
			</div>
		</>
	)
}

// ── Status chip ────────────────────────────────────────────────────────────────

function StatusChip({ tone, label }: { tone: PastelTone; label: string }) {
	return (
		<span
			className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${CHIP_TONES[tone]}`}
		>
			{label}
		</span>
	)
}

// ── Score panel (mirrors ScorePanelView exactly) ───────────────────────────────

const CHIP_CLASS =
	'flex h-16 w-full items-center justify-center gap-2.5 rounded-2xl border border-[#F0F0F0] bg-[#fbfdfd] px-6 text-[15px] font-medium'

const GUARANTOR_TONE_TEXT: Record<'green' | 'amber' | 'red', string> = {
	green: 'text-[#16A34A]',
	amber: 'text-[#F59F0A]',
	red: 'text-[#DC2626]',
}

function DemoScorePanel({ d }: { d: ReportData }) {
	return (
		<div className="flex min-w-0 flex-1 basis-130 flex-wrap items-center justify-end gap-4 lg:basis-105">
			{/* Score box */}
			<div className="flex w-full shrink-0 flex-col items-center rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] px-8 py-6 lg:w-52.5">
				<p className="leading-none">
					<span className="text-[58px] font-extrabold tracking-tight text-[#32302F]">
						{d.score}
					</span>
					<span className="text-[26px] font-medium text-[#747474]">/100</span>
				</p>
				<div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#F3F3F3]">
					<div
						className="h-full rounded-full bg-[#16A34A]"
						style={{ width: `${d.score}%` }}
					/>
				</div>
			</div>
			{/* Chips */}
			{(d.showGuarantor || d.showGuarantee) && (
				<div className="flex max-w-105 min-w-70 flex-1 flex-col gap-3">
					{d.showGuarantor && (
						<div
							className={`${CHIP_CLASS} ${GUARANTOR_TONE_TEXT[d.guarantorTone]}`}
						>
							<Shield className="size-5 shrink-0" />
							{d.guarantorLabel}
						</div>
					)}
					{d.showGuarantee && (
						<div className={`${CHIP_CLASS} text-[#16A34A]`}>
							<Shield className="size-5 shrink-0" />
							{d.guaranteeLabel}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function DemoReport() {
	const [searchParams] = useSearchParams()
	const [variant, setVariant] = useState<ReportVariant>(() =>
		parseVariant(searchParams.get('variant')),
	)
	const d = REPORT_DATA[variant]
	const isFull = d.kind === 'full'

	return (
		<div className="min-h-screen bg-[#fbfdfd]">
			{/* ── Sticky top bar ── */}
			<div className="sticky top-0 z-50 border-b border-[#EDEDEB] bg-[#fbfdfd] shadow-[0px_4px_4px_0px_#0000000D]">
				<div className="mx-auto w-full max-w-442.5 px-4 sm:px-8 flex min-h-16 flex-wrap items-center justify-between gap-3 py-3 pr-4 lg:pr-55">
					<div className="flex min-w-0 items-center gap-3">
						<Link
							to="/demo"
							className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm font-medium text-[#32302F]"
						>
							<ChevronLeft className="size-5 shrink-0 text-gray-500" />
							<span>Back to Demo</span>
						</Link>
						<span className="h-5 w-px shrink-0 bg-[#E2E2E0]" />
						<span className="truncate text-sm font-semibold text-[#1F1E1D]">
							{d.applicantName}
						</span>
					</div>
					<StatusChip tone={d.statusTone} label={d.statusLabel} />
				</div>
			</div>

			<DemoSidebar current={variant} onChange={setVariant} />

			{/* ── Page content ── */}
			<div className="mx-auto w-full max-w-442.5 px-4 sm:px-8 pb-20 lg:pb-8 lg:pr-55">
				{/* ── Header card ── */}
				<div className="mt-6">
					<div
						className={`flex flex-wrap items-center gap-4 ${CARD} p-5 sm:gap-6 sm:p-7 xl:flex-nowrap`}
					>
						{/* Avatar + name */}
						<div className="flex min-w-0 items-center gap-4 sm:gap-6 xl:flex-1">
							<div className="size-20 sm:size-26 rounded-full border-2 border-[#32302F] flex items-center justify-center bg-[#F4F4F2] shrink-0">
								<span className="text-2xl font-semibold text-[#32302F]">
									{d.initials}
								</span>
							</div>
							<div className="min-w-0">
								<h1 className="text-[25px] leading-tight font-medium text-[#1F1E1D] md:text-[30px] lg:text-[40px]">
									{d.applicantName}
								</h1>
								<p className="mt-1 text-sm text-[#747474]">{d.subtitle}</p>
								{/* Household badges — children / pets / vehicles */}
								<div className="mt-3 flex flex-wrap items-center gap-2.5">
									{d.children !== 'None' &&
										(() => {
											const num = d.children.match(/^(\d+)/)?.[1] ?? '1'
											return (
												<span
													key="children"
													className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium [&>svg]:size-4 ${PASTEL_TONES.purple}`}
												>
													<Baby />
													{`${num} ${Number(num) === 1 ? 'Child' : 'Children'}`}
												</span>
											)
										})()}
									{d.pets !== 'None declared' && d.pets !== 'None' && (
										<span
											className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium [&>svg]:size-4 ${PASTEL_TONES.rose}`}
										>
											<PawPrint />
											{d.pets.split('·')[0].trim()}
										</span>
									)}
									{d.vehicles !== 'None declared' && d.vehicles !== 'None' && (
										<span
											className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium [&>svg]:size-4 ${PASTEL_TONES.green}`}
										>
											<Car />
											{d.vehicles.split('·')[0].trim()}
										</span>
									)}
								</div>
							</div>
						</div>
						{/* Score panel (full) or Upsell banner (pre-screen) */}
						{isFull ? (
							<div className="flex w-full min-w-0 justify-end xl:w-auto xl:shrink-0">
								<DemoScorePanel d={d} />
							</div>
						) : (
							<div className="min-w-0 flex-1 max-[1050px]:basis-auto max-[1000px]:basis-full">
								<div className="flex flex-wrap items-center gap-5 rounded-2xl bg-[#f4f6f6] p-5">
									<span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#FFF6EA] text-[#F59F0A]">
										<Crown className="size-6" />
									</span>
									<div className="min-w-0 flex-1 basis-52">
										<p className="text-base font-semibold text-[#32302F]">
											Approve with proof, not a hunch
										</p>
										<p className="mt-1 text-[13px] leading-snug text-gray-500">
											Make smarter leasing decisions with access to our most
											detailed screening reports and verification data.
										</p>
									</div>
									<button
										type="button"
										onClick={() => setVariant('good')}
										className="shrink-0 rounded-full bg-[#32302F] px-7 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-[#1a1918] cursor-pointer"
									>
										Proceed To Full Screen
									</button>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* ── Sections ── */}
				<div className="mt-6 flex flex-col gap-6">
					{/* AI Summary (full only) */}
					{isFull && (
						<section className={`${CARD} p-6 sm:p-7`}>
							<div className="flex items-center gap-3">
								<span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#FBFDFD] shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
									<Sparkle className="size-5 text-[#32302F]" />
								</span>
								<h2 className="text-[16px] font-medium text-[#32302F]">
									AI Generated Summary
								</h2>
							</div>
							<p className="mt-4 text-[16px] leading-relaxed text-[#32302F]">
								{d.aiSummary}
							</p>
						</section>
					)}

					{/* Summary Tiles */}
					<section className={`${CARD} p-6 sm:p-7`}>
						<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
							{[
								{
									tone: 'green' as PastelTone,
									icon: <CircleDollarSign />,
									label: 'Rental Range Eligibility',
									value: d.rentalRange,
								},
								{
									tone: 'rose' as PastelTone,
									icon: <CalendarDays />,
									label: 'Move-In Date',
									value: d.moveInDate,
								},
								{
									tone: 'purple' as PastelTone,
									icon: <HandCoins />,
									label: 'Savings & Investments',
									value: d.savings,
								},
							].map(({ tone, icon, label, value }) => (
								<div
									key={label}
									className="flex flex-col items-center gap-3 rounded-2xl border border-[#F0F0F0] bg-[#fbfdfd] px-6 py-6 text-center"
								>
									<IconSquare tone={tone} className="size-11 rounded-xl">
										{icon}
									</IconSquare>
									<span className="text-sm text-[#747474]">{label}</span>
									<span className="-mt-1 text-[22px] font-semibold text-[#32302F]">
										{value}
									</span>
								</div>
							))}
						</div>
					</section>

					{/* Verification Summary */}
					<section className={`${CARD} p-6 sm:p-7`}>
						<h2 className="text-[16px] font-semibold text-[#32302F]">
							Verification Summary
						</h2>
						<div
							className={`mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 ${isFull ? 'xl:grid-cols-5' : 'xl:grid-cols-3'}`}
						>
							{[
								{
									tone: 'green' as PastelTone,
									icon: <ShieldCheck />,
									title: 'ID Verification',
									tile: d.verifs.id,
								},
								{
									tone: 'rose' as PastelTone,
									icon: <Banknote />,
									title: 'Income Verification',
									tile: d.verifs.income,
								},
								{
									tone: 'green' as PastelTone,
									icon: <CircleDollarSign />,
									title: 'Rent Verification',
									tile: d.verifs.rent,
								},
								...(isFull
									? [
											{
												tone: 'purple' as PastelTone,
												icon: <FileText />,
												title: 'Court Records',
												tile: d.verifs.court,
											},
											{
												tone: 'rose' as PastelTone,
												icon: <ClipboardPlus />,
												title: 'Credit Report',
												tile: d.verifs.credit,
											},
										]
									: []),
							].map(({ tone, icon, title, tile }) => (
								<div
									key={title}
									className={`relative flex flex-col items-center gap-2 rounded-2xl border border-t-[5px] border-[#F0F0F0] bg-[#FBFDFD] px-6 pt-6 pb-4 text-center ${BAR_TONES[tile.status]}`}
								>
									<IconSquare tone={tone}>{icon}</IconSquare>
									<span className="text-[17px] font-semibold text-[#32302F]">
										{title}
									</span>
									{tile.onClick ? (
										<button
											type="button"
											onClick={tile.onClick}
											className="cursor-pointer text-sm text-[#747474] transition-colors hover:text-[#32302F]"
										>
											{tile.text}
										</button>
									) : (
										<span className="text-sm text-[#747474]">{tile.text}</span>
									)}
								</div>
							))}
						</div>
					</section>

					{/* Risk Flags (only when flags exist) */}
					{(d.redFlags.length > 0 || d.yellowFlags.length > 0) && (
						<section
							id="risk-flags"
							className={`${CARD} scroll-mt-28 p-6 sm:p-7`}
						>
							<h2 className="text-[16px] font-semibold text-[#32302F]">
								Risk Flags & Alerts
							</h2>
							<div className="mt-5 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
								{d.redFlags.length > 0 && (
									<div className="rounded-2xl p-6 bg-[#FFF7F7]">
										<h3 className="text-[17px] font-semibold text-[#EF4343]">
											Red Flags
										</h3>
										<ul className="mt-4 space-y-4">
											{d.redFlags.map((msg) => (
												<li
													key={msg}
													className="flex items-start gap-2.5 text-[15px] text-[#747474]"
												>
													<FlagTriangleRight className="mt-1 size-4 shrink-0 text-[#EF4343]" />
													<span>{msg}</span>
												</li>
											))}
										</ul>
									</div>
								)}
								{d.yellowFlags.length > 0 && (
									<div className="rounded-2xl p-6 bg-[#F59F0A0D]">
										<h3 className="text-[17px] font-semibold text-[#F59F0A]">
											Yellow Flags
										</h3>
										<ul className="mt-4 space-y-4">
											{d.yellowFlags.map((msg) => (
												<li
													key={msg}
													className="flex items-start gap-2.5 text-[15px] text-[#747474]"
												>
													<FlagTriangleRight className="mt-1 size-4 shrink-0 text-[#F59F0A]" />
													<span>{msg}</span>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						</section>
					)}

					{/* Debt Summary (full only) */}
					{isFull && (
						<section className={`${CARD} p-6 sm:p-7`}>
							<div className="flex flex-wrap items-center justify-between gap-4">
								<h2 className="text-[16px] font-semibold text-[#32302F]">
									Debt Summary
								</h2>
								<div className="flex flex-wrap items-center gap-3">
									<span className="inline-flex items-baseline gap-2 rounded-xl border border-[#F0F0F0] bg-[#FBFDFD] px-4 py-2.5">
										<span className="text-sm text-[#747474]">Monthly</span>
										<span className="text-[16px] font-medium text-[#32302F]">
											{d.monthlyDebt}
										</span>
									</span>
									<span className="inline-flex items-baseline gap-2 rounded-xl border border-[#F0F0F0] bg-[#FBFDFD] px-4 py-2.5">
										<span className="text-sm text-[#747474]">Total Owing</span>
										<span className="text-[16px] font-medium text-[#32302F]">
											{d.totalOwing}
										</span>
									</span>
								</div>
							</div>
							{/* Bank monitoring sub-section */}
							<div className="mt-6">
								<h3 className="text-sm font-medium text-[#747474] uppercase tracking-wide mb-4">
									Bank Monitoring (Flinks)
								</h3>
								<div className="grid grid-cols-3 gap-4 mb-5">
									{[
										{ label: 'Avg Balance', value: d.avgBalance },
										{ label: 'Income-to-Rent', value: d.incomeToRent },
										{ label: 'Overdrafts (90d)', value: d.overdrafts },
									].map(({ label, value }) => (
										<div
											key={label}
											className="rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] p-5 text-center"
										>
											<span className="block text-sm text-[#747474]">
												{label}
											</span>
											<span
												className={`mt-1 block text-[17px] font-medium ${d.overdrafts !== '0' && label === 'Overdrafts (90d)' ? 'text-[#EF4343]' : 'text-[#32302F]'}`}
											>
												{value}
											</span>
										</div>
									))}
								</div>
								<div className="overflow-hidden rounded-[14px] border border-[#F0F0F0]">
									<table className="w-full table-fixed">
										<thead>
											<tr className="border-b border-[#F0F0F0] bg-[#FBFDFD]">
												{['Date', 'Description', 'Amount', 'Balance'].map(
													(h) => (
														<th
															key={h}
															className="h-14 px-4 text-left text-[15px] font-normal text-[#747474]"
														>
															{h}
														</th>
													),
												)}
											</tr>
										</thead>
										<tbody>
											{d.transactions.map((tx) => (
												<tr
													key={`${tx.date}-${tx.desc}`}
													className="border-t border-[#F0F0F0]"
												>
													<td className="h-14 px-4 text-[14px] text-[#747474]">
														{tx.date}
													</td>
													<td className="h-14 px-4 text-[14px] text-[#32302F]">
														{tx.desc}
													</td>
													<td
														className={`h-14 px-4 text-[14px] font-medium ${tx.positive ? 'text-[#16A34A]' : 'text-[#32302F]'}`}
													>
														{tx.amount}
													</td>
													<td className="h-14 px-4 text-[14px] text-[#747474]">
														{tx.balance}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</section>
					)}

					{/* Collections (full only) */}
					{isFull && d.collections.length > 0 && (
						<section className={`${CARD} p-6 sm:p-7`}>
							<div className="flex flex-wrap items-center justify-between gap-4">
								<h2 className="text-[16px] font-semibold text-[#32302F]">
									Collections
								</h2>
								<span className="inline-flex items-baseline gap-2 rounded-xl border border-[#F0F0F0] bg-[#FBFDFD] px-4 py-2.5">
									<span className="text-sm text-[#747474]">Total Owing</span>
									<span className="text-[16px] font-medium text-[#32302F]">
										$2,100
									</span>
								</span>
							</div>
							<div className="mt-5 overflow-hidden rounded-[14px] border border-[#F0F0F0]">
								<table className="w-full table-fixed">
									<thead>
										<tr className="border-b border-[#F0F0F0] bg-[#FBFDFD]">
											{[
												'Creditor',
												'Balance',
												'Opened',
												'Status',
												'Remarks',
											].map((h) => (
												<th
													key={h}
													className="h-16 px-4 text-center text-[15px] font-normal text-[#747474]"
												>
													{h}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{d.collections.map((row) => (
											<tr
												key={row.creditor}
												className="border-t border-[#F0F0F0]"
											>
												<td className="h-15 px-4 text-center text-[15px] text-[#32302F]">
													{row.creditor}
												</td>
												<td className="h-15 px-4 text-center text-[15px] text-[#32302F]">
													{row.balance}
												</td>
												<td className="h-15 px-4 text-center text-[15px] text-[#32302F]">
													{row.opened}
												</td>
												<td className="h-15 px-4 text-center">
													<span className="inline-flex rounded-full px-3 py-1 text-[13px] font-medium bg-[#FDF4F5] text-[#DC2634]">
														{row.status}
													</span>
												</td>
												<td className="h-15 px-4 text-center text-[14px] text-[#747474]">
													{row.remarks}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					)}

					{/* Personal + Household (2-col grid) */}
					<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
						<SectionCard
							tone="purple"
							icon={<FileUser />}
							title="Personal Details"
						>
							<FieldBox label="First Name" value={d.firstName} />
							<FieldBox label="Last Name" value={d.lastName} />
							<FieldBox label="Email" value={d.email} />
							<FieldBox label="Phone" value={d.phone} />
							<FieldBox label="Applied" value={d.appDate} />
							<FieldBox label="Type" value={d.appType} />
						</SectionCard>
						<SectionCard tone="green" icon={<User />} title="Household Details">
							<FieldBox label="Adults" value={d.adults} />
							<FieldBox label="Children" value={d.children} />
							<FieldBox label="Pets" value={d.pets} />
							<FieldBox label="Vehicles" value={d.vehicles} />
						</SectionCard>
					</div>

					{/* Employment */}
					<section className={`${CARD} p-6 sm:p-7`}>
						<div className="flex items-center gap-3 mb-6">
							<IconSquare tone="amber">
								<TrendingUp />
							</IconSquare>
							<h2 className="text-[16px] font-semibold text-[#32302F]">
								Employment
							</h2>
						</div>
						<div className="space-y-6">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-[#747474] mb-4">
									Current Employment
								</p>
								<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
									<FieldBox label="Company" value={d.curCompany} />
									<FieldBox label="Occupation" value={d.curOccupation} />
									<FieldBox label="Annual Income" value={d.curAnnualIncome} />
									<FieldBox
										label="Employment Type"
										value={d.curEmploymentType}
									/>
									<FieldBox label="Duration" value={d.curYears} />
									<FieldBox label="Work Address" value={d.curWorkAddress} />
								</div>
							</div>
							<div className="border-t border-[#F0F0F0] pt-6">
								<p className="text-xs font-semibold uppercase tracking-wide text-[#747474] mb-4">
									Previous Employment
								</p>
								<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
									<FieldBox label="Company" value={d.prevCompany} />
									<FieldBox label="Occupation" value={d.prevOccupation} />
									<FieldBox label="Income" value={d.prevAnnualIncome} />
									<FieldBox label="Dates" value={d.prevDates} />
								</div>
							</div>
						</div>
					</section>

					{/* Rental History */}
					<section className={`${CARD} p-6 sm:p-7`}>
						<div className="flex items-center gap-3 mb-6">
							<IconSquare tone="rose">
								<Home />
							</IconSquare>
							<h2 className="text-[16px] font-semibold text-[#32302F]">
								Rental History
							</h2>
						</div>
						<div className="space-y-6">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-[#747474] mb-4">
									Currently Renting
								</p>
								<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
									<FieldBox label="Landlord" value={d.landlordName} />
									<FieldBox label="Phone" value={d.landlordPhone} />
									<FieldBox label="Email" value={d.landlordEmail} />
									<FieldBox label="Address" value={d.curRentAddress} />
									<FieldBox label="Current Rent" value={d.curRent} />
									<FieldBox label="Payment Method" value={d.curPaymentMethod} />
									<FieldBox label="Duration" value={d.rentalDuration} />
									<FieldBox label="N4 Received" value={d.n4} />
									<FieldBox label="L1 Received" value={d.l1} />
								</div>
							</div>
							<div className="border-t border-[#F0F0F0] pt-6">
								<p className="text-xs font-semibold uppercase tracking-wide text-[#747474] mb-4">
									Previous Rental
								</p>
								<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
									<FieldBox label="Landlord" value={d.prevLandlord} />
									<FieldBox label="Address" value={d.prevRentAddress} />
									<FieldBox label="Previous Rent" value={d.prevRent} />
									<FieldBox
										label="Payment Method"
										value={d.prevPaymentMethod}
									/>
									<FieldBox label="Dates" value={d.prevRentalDates} />
									<FieldBox label="N4 Received" value={d.prevN4} />
									<FieldBox label="L1 Received" value={d.prevL1} />
								</div>
							</div>
						</div>
					</section>

					{/* Additional Notes */}
					{d.additionalNotes && (
						<section className={`${CARD} p-6 sm:p-7`}>
							<div className="flex items-center gap-3 mb-4">
								<IconSquare tone="neutral">
									<FileText />
								</IconSquare>
								<h2 className="text-[16px] font-semibold text-[#32302F]">
									Additional Notes from Tenant
								</h2>
							</div>
							<p className="text-[16px] leading-relaxed text-[#32302F]">
								{d.additionalNotes}
							</p>
						</section>
					)}
				</div>
			</div>
		</div>
	)
}
